<template>
  <div class="p-6">
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
      <div>
        <button @click="$router.push('/analytics')" class="text-zinc-400 hover:text-white transition-colors mb-2 flex items-center gap-2 text-sm">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
          Retour aux statistiques
        </button>
        <h1 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-rose-500">
          Artistes non trouvés
        </h1>
        <p class="text-zinc-400 mt-2">
          Ces artistes de votre bibliothèque n'ont pas pu être associés à une fiche MusicBrainz. 
          ({{ filteredArtists.length }} / {{ artists.length }})
        </p>
      </div>

      <div class="relative w-full md:w-64">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input 
          v-model="searchQuery" 
          type="text" 
          placeholder="Rechercher un artiste..." 
          class="block w-full pl-10 pr-3 py-2 border border-zinc-700 rounded-xl leading-5 bg-zinc-800/50 text-zinc-300 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 sm:text-sm transition-colors"
        >
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-20">
      <div class="w-12 h-12 border-4 border-red-500/30 border-t-red-500 rounded-full animate-spin"></div>
    </div>
    
    <div v-else-if="filteredArtists.length === 0" class="text-center py-20 bg-zinc-800/20 rounded-3xl border border-zinc-800 border-dashed">
      <svg class="w-16 h-16 text-zinc-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path></svg>
      <h3 class="text-xl font-medium text-zinc-300">Aucun artiste trouvé</h3>
      <p class="text-zinc-500 mt-2">Essayez de modifier votre recherche.</p>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <div 
        v-for="artist in filteredArtists" 
        :key="artist.spotify_id"
        class="bg-zinc-800/40 border border-zinc-700/50 rounded-2xl p-5 hover:bg-zinc-800/60 hover:border-red-500/30 transition-all group flex flex-col"
      >
        <div class="flex items-center gap-4 mb-4">
          <div class="w-16 h-16 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform">
            <img v-if="artist.images && artist.images.length" :src="artist.images[0].url" :alt="artist.name" class="w-full h-full object-cover">
            <div v-else class="w-full h-full flex items-center justify-center text-zinc-600">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="font-bold text-white truncate text-lg" :title="artist.name">{{ artist.name }}</h3>
            <p class="text-sm text-zinc-400">{{ artist.track_count }} titres aimés</p>
          </div>
        </div>

        <div class="flex gap-2 mb-4">
          <a :href="`https://musicbrainz.org/search?query=${encodeURIComponent(artist.name)}&type=artist`" target="_blank" class="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs py-2 px-3 rounded-lg text-center transition-colors flex items-center justify-center gap-1">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            Rechercher
          </a>
          <a :href="`https://musicbrainz.org/artist/create?edit-artist.name=${encodeURIComponent(artist.name)}`" target="_blank" class="flex-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 text-xs py-2 px-3 rounded-lg text-center transition-colors flex items-center justify-center gap-1">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path></svg>
            Créer
          </a>
        </div>

        <div class="mt-auto border-t border-zinc-700 pt-3">
          <label class="block text-xs text-zinc-500 mb-1">Forcer l'ID MusicBrainz (MBID) :</label>
          <div class="flex gap-2">
            <input 
              v-model="mbidInputs[artist.spotify_id]" 
              type="text" 
              placeholder="ex: 12345678-..." 
              class="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-2 text-sm text-zinc-300 focus:outline-none focus:border-red-500 min-w-0"
            >
            <button 
              @click="saveMbid(artist)" 
              :disabled="!mbidInputs[artist.spotify_id] || savingId === artist.spotify_id"
              class="bg-red-500 hover:bg-red-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-white p-2 rounded-lg transition-colors flex items-center justify-center flex-shrink-0"
              title="Enregistrer"
            >
              <svg v-if="savingId !== artist.spotify_id" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
              <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from '../../composables/useToast'

const artists = ref<any[]>([])
const loading = ref(true)
const searchQuery = ref('')
const mbidInputs = ref<Record<string, string>>({})
const savingId = ref<string | null>(null)
const { success: showToast, error: showError } = useToast()

const filteredArtists = computed(() => {
  if (!searchQuery.value) return artists.value
  const query = searchQuery.value.toLowerCase()
  return artists.value.filter(a => a.name.toLowerCase().includes(query))
})

const fetchArtists = async () => {
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/analytics/artists/missing-mbid', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!response.ok) throw new Error('Erreur réseau')
    artists.value = await response.json()
  } catch (e) {
    showError("Erreur lors de la récupération des artistes")
  } finally {
    loading.value = false
  }
}

const saveMbid = async (artist: any) => {
  const mbid = mbidInputs.value[artist.spotify_id]
  if (!mbid) return

  savingId.value = artist.spotify_id
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch(`/api/music/analytics/artist/${artist.spotify_id}/mbid`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ mbid })
    })

    if (!response.ok) throw new Error("Erreur lors de la mise à jour")
    
    // Copier le lien Spotify dans le presse-papier pour faciliter l'ajout
    const spotifyUrl = `https://open.spotify.com/artist/${artist.spotify_id}`
    try {
      await navigator.clipboard.writeText(spotifyUrl)
      showToast(`MBID enregistré ! Le lien Spotify a été copié.`, 'success')
    } catch (err) {
      showToast(`MBID enregistré pour ${artist.name}`, 'success')
    }

    // Ouvrir la page d'édition de l'artiste sur MusicBrainz
    window.open(`https://musicbrainz.org/artist/${mbid}/edit`, '_blank')

    // Retirer l'artiste de la liste
    artists.value = artists.value.filter(a => a.spotify_id !== artist.spotify_id)
  } catch (e) {
    showError("Impossible d'enregistrer l'ID")
  } finally {
    savingId.value = null
  }
}

onMounted(() => {
  fetchArtists()
})
</script>
